import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CityMap, { TrafficLight, Vehicle, routes, StopPoint } from '../components/CityMap';
import './TrafficControl.css';

const CAR_COLORS = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#ecf0f1'];

// Berechnet die effektive Ampelfarbe für eine bestimmte Richtung (nur Rot/Grün)
function getLightColorForDirection(light: TrafficLight, direction: StopPoint['direction']): 'red' | 'green' {
  // NS und Ring bekommen den Hauptzustand
  if (direction === 'ns' || direction === 'ring') {
    return light.state === 'green' ? 'green' : 'red';
  }
  // EW und Diagonal bekommen das Gegenteil (invertiert)
  return light.state === 'green' ? 'red' : 'green';
}

const initialTrafficLights: TrafficLight[] = [
  // Altstadtring - koordiniert für Durchgangsverkehr
  { id: 'inner-n', x: 600, y: 280, state: 'green' },   // NS grün - Radiale kann einfahren
  { id: 'inner-s', x: 600, y: 530, state: 'green' },   // NS grün - Radiale kann einfahren
  { id: 'inner-w', x: 468, y: 400, state: 'green' },   // NS grün - Ring-Verkehr kann durch
  { id: 'inner-e', x: 732, y: 400, state: 'green' },   // NS grün - Ring-Verkehr kann durch
  // Mittlerer Ring
  { id: 'outer-n', x: 600, y: 100, state: 'green' },
  { id: 'outer-s', x: 600, y: 700, state: 'green' },
  { id: 'outer-w', x: 200, y: 400, state: 'red' },
  { id: 'outer-e', x: 1000, y: 400, state: 'red' },
  { id: 'outer-nw', x: 280, y: 200, state: 'green' },
  { id: 'outer-ne', x: 920, y: 200, state: 'green' },
  { id: 'outer-sw', x: 280, y: 600, state: 'green' },
  { id: 'outer-se', x: 920, y: 600, state: 'green' },
];

export default function TrafficControl() {
  const { t } = useTranslation();
  const [trafficLights, setTrafficLights] = useState<TrafficLight[]>(initialTrafficLights);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [maxVehicles, setMaxVehicles] = useState(50);
  const vehicleIdRef = useRef(0);
  const lastSpawnRef = useRef(0);

  const handleIntersectionClick = (id: string) => {
    setTrafficLights(prev =>
      prev.map(light => {
        if (light.id !== id) return light;
        // Nur Rot und Grün - kein Gelb
        const nextState: TrafficLight['state'] = light.state === 'red' ? 'green' : 'red';
        return { ...light, state: nextState };
      })
    );
  };

  // Spawn neues Fahrzeug
  const spawnVehicle = useCallback(() => {
    const availableRoutes = routes.filter(r => r.stopPoints.length > 0); // Nur Routen mit Ampeln
    if (availableRoutes.length === 0) return;

    const route = availableRoutes[Math.floor(Math.random() * availableRoutes.length)];
    const maxSpeed = 0.003 + Math.random() * 0.002; // Variable Maximalgeschwindigkeit
    const newVehicle: Vehicle = {
      id: `car-${vehicleIdRef.current++}`,
      routeId: route.id,
      progress: 0,
      speed: maxSpeed, // Startet mit voller Geschwindigkeit
      maxSpeed,
      color: CAR_COLORS[Math.floor(Math.random() * CAR_COLORS.length)],
    };

    setVehicles(prev => [...prev, newVehicle]);
  }, []);

  // Simulations-Loop
  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      const now = Date.now();

      // Spawne neue Autos alle 300-600ms
      if (now - lastSpawnRef.current > 300 + Math.random() * 300) {
        if (vehicles.length < maxVehicles) {
          spawnVehicle();
          lastSpawnRef.current = now;
        }
      }

      // Update Fahrzeug-Positionen
      setVehicles(prev => {
        const updated = prev.map(vehicle => {
          const route = routes.find(r => r.id === vehicle.routeId);
          if (!route) return vehicle;

          // Konstanten für Beschleunigung/Verzögerung
          const ACCELERATION = 0.0003;  // Beschleunigung pro Frame
          const DECELERATION = 0.0006;  // Bremsen pro Frame (stärker als beschleunigen)
          const MIN_FOLLOW_DISTANCE = 0.025; // Minimaler Abstand (wenn stehend) - groß genug für sichtbare Lücke
          const SPEED_DISTANCE_FACTOR = 8;   // Abstand = MIN + speed * FACTOR

          let targetSpeed = vehicle.maxSpeed; // Standardmäßig Vollgas
          let mustStop = false;

          // Finde den nächsten Stop-Point VOR dem Auto
          const nextStop = route.stopPoints.find(sp => sp.progress > vehicle.progress);

          if (nextStop) {
            const distanceToStop = nextStop.progress - vehicle.progress;
            const STOP_DISTANCE = 0.006; // Wo das Auto stehen bleibt (sehr nah an Ampel)
            const BRAKE_START = 0.018;   // Ab wann gebremst wird

            // Stop-Zone: STOP_DISTANCE bis BRAKE_START
            if (distanceToStop <= BRAKE_START && distanceToStop > STOP_DISTANCE) {
              const light = trafficLights.find(l => l.id === nextStop.intersectionId);
              if (light) {
                const effectiveColor = getLightColorForDirection(light, nextStop.direction);
                if (effectiveColor === 'red') {
                  mustStop = true;
                  // Je näher an der Ampel, desto langsamer
                  targetSpeed = Math.max(0, (distanceToStop - STOP_DISTANCE) / (BRAKE_START - STOP_DISTANCE)) * vehicle.maxSpeed;
                }
              }
            }
          }

          // Finde das nächste Auto vor uns auf derselben Route
          const carsAhead = prev.filter(v =>
            v.routeId === vehicle.routeId &&
            v.id !== vehicle.id &&
            v.progress > vehicle.progress
          );

          // Finde das NÄCHSTE Auto (kleinstes progress das größer ist)
          const carAhead = carsAhead.length > 0
            ? carsAhead.reduce((closest, v) => v.progress < closest.progress ? v : closest)
            : null;

          // Maximale Position (kann nicht überholen)
          let maxProgress = 1.0;

          if (carAhead) {
            const distanceToCarAhead = carAhead.progress - vehicle.progress;
            const BRAKE_DISTANCE = MIN_FOLLOW_DISTANCE + 0.015; // Ab wann bremsen

            // Harte Grenze: niemals näher als MIN_FOLLOW_DISTANCE
            maxProgress = carAhead.progress - MIN_FOLLOW_DISTANCE;

            if (distanceToCarAhead < BRAKE_DISTANCE) {
              mustStop = true;
              // Wie bei Ampel: je näher, desto langsamer
              targetSpeed = Math.min(targetSpeed,
                Math.max(0, (distanceToCarAhead - MIN_FOLLOW_DISTANCE) / (BRAKE_DISTANCE - MIN_FOLLOW_DISTANCE)) * vehicle.maxSpeed
              );

              // Wenn am Mindestabstand angekommen, stoppen
              if (distanceToCarAhead <= MIN_FOLLOW_DISTANCE) {
                targetSpeed = 0;
              }
            }
          }

          // Geschwindigkeit anpassen (sanft beschleunigen/bremsen)
          let newSpeed = vehicle.speed;
          if (targetSpeed < vehicle.speed) {
            // Bremsen
            newSpeed = Math.max(targetSpeed, vehicle.speed - DECELERATION);
          } else if (targetSpeed > vehicle.speed) {
            // Beschleunigen
            newSpeed = Math.min(targetSpeed, vehicle.speed + ACCELERATION);
          }

          // Wenn komplett gestoppt und muss stoppen, bleib bei 0
          if (mustStop && newSpeed < 0.0002) {
            newSpeed = 0;
          }

          // Bewege das Auto (aber nie über maxProgress hinaus - kein Überholen)
          let newProgress = vehicle.progress + newSpeed;
          newProgress = Math.min(newProgress, maxProgress);

          // Entferne Auto wenn am Ende der Route
          if (newProgress >= 1) {
            return { ...vehicle, progress: 1.1, speed: newSpeed };
          }

          return { ...vehicle, progress: newProgress, speed: newSpeed };
        });

        // Entferne Autos die die Route beendet haben
        return updated.filter(v => v.progress <= 1);
      });
    }, 33); // ~30 FPS

    return () => clearInterval(interval);
  }, [isRunning, vehicles.length, trafficLights, spawnVehicle, maxVehicles]);

  // Automatische Ampelschaltung
  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      setTrafficLights(prev => prev.map(light => {
        // Nur Rot und Grün - kein Gelb
        const nextState: TrafficLight['state'] = light.state === 'red' ? 'green' : 'red';
        return { ...light, state: nextState };
      }));
    }, 5000); // Alle 5 Sekunden

    return () => clearInterval(interval);
  }, [isRunning]);

  const toggleSimulation = () => {
    if (!isRunning) {
      lastSpawnRef.current = Date.now();
    }
    setIsRunning(!isRunning);
  };

  const resetSimulation = () => {
    setIsRunning(false);
    setVehicles([]);
    setTrafficLights(initialTrafficLights);
  };

  return (
    <div className="traffic-control">
      <header className="traffic-header">
        <h1>{t('trafficControl.title')}</h1>
        <p className="traffic-intro">{t('trafficControl.intro')}</p>
      </header>

      <section className="controls-section">
        <button
          className={`sim-button ${isRunning ? 'running' : ''}`}
          onClick={toggleSimulation}
        >
          {isRunning ? '⏸ Pause' : '▶ Start'}
        </button>
        <button className="sim-button reset" onClick={resetSimulation}>
          ↺ Reset
        </button>
        <span className="vehicle-count">
          🚗 {vehicles.length}/{maxVehicles}
        </span>
        <div className="vehicle-slider">
          <input
            type="range"
            min="5"
            max="100"
            value={maxVehicles}
            onChange={(e) => setMaxVehicles(Number(e.target.value))}
          />
        </div>
      </section>

      <section className="map-section">
        <CityMap
          trafficLights={trafficLights}
          vehicles={vehicles}
          onIntersectionClick={handleIntersectionClick}
        />
        <p className="map-hint">{t('trafficControl.mapHint')}</p>
      </section>

      <section className="legend-section">
        <h2>{t('trafficControl.legend.title')}</h2>
        <div className="legend-grid">
          <div className="legend-item">
            <span className="legend-line highway"></span>
            <span>{t('trafficControl.legend.highway')}</span>
          </div>
          <div className="legend-item">
            <span className="legend-line major"></span>
            <span>{t('trafficControl.legend.majorRoad')}</span>
          </div>
          <div className="legend-item">
            <span className="legend-line minor"></span>
            <span>{t('trafficControl.legend.minorRoad')}</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot red"></span>
            <span>{t('trafficControl.legend.redLight')}</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot green"></span>
            <span>{t('trafficControl.legend.greenLight')}</span>
          </div>
        </div>
      </section>
    </div>
  );
}
